/**
 * typography.ts — Tipografia governada do Studio
 *
 * Fonte ÚNICA das famílias de fonte e dos ajustes de título usados pelo editor,
 * pela página publicada, pelo PDF e pelo e-mail. O documento NUNCA informa um
 * endereço de fonte: informa apenas uma chave de conjunto aprovado. Assim não
 * existe carregamento externo nem valor de CSS vindo do usuário.
 *
 * RELEASE: v1.0.0 - 2026-09-11 - Versão inicial (vocabulário editorial, Fase 2)
 */

/** Conjunto de fontes aprovado (corpo + título). */
export interface StudioFontPreset {
  key: string;
  label: string;
  /** Pilha de fontes do texto corrido. */
  body: string;
  /** Pilha de fontes dos títulos. */
  heading: string;
}

const SISTEMA = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif";

/**
 * Conjuntos aprovados. Todos usam fontes já presentes nos sistemas operacionais,
 * o que mantém a página leve e dispensa liberar domínio de fonte na política de
 * conteúdo.
 */
export const STUDIO_FONT_PRESETS: StudioFontPreset[] = [
  { key: 'system', label: 'Sistema (padrão)', body: SISTEMA, heading: SISTEMA },
  {
    key: 'editorial',
    label: 'Editorial (título com serifa)',
    body: SISTEMA,
    heading: "Georgia, 'Times New Roman', 'Iowan Old Style', serif",
  },
  {
    key: 'geometric',
    label: 'Geométrico (título condensado)',
    body: SISTEMA,
    heading: "'Archivo', 'Helvetica Neue', 'Arial Narrow', Impact, sans-serif",
  },
  {
    key: 'mono-accent',
    label: 'Técnico (título monoespaçado)',
    body: SISTEMA,
    heading: "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace",
  },
];

/** Opções prontas para o painel de propriedades e para o catálogo. */
export const STUDIO_FONT_PRESET_OPTIONS = STUDIO_FONT_PRESETS.map((preset) => ({
  value: preset.key,
  label: preset.label,
}));

/** Peso de título aceito. */
export const STUDIO_HEADING_WEIGHTS = ['400', '500', '600', '700', '800', '900'];

/** Caixa de título aceita. */
export const STUDIO_HEADING_TRANSFORMS = ['none', 'uppercase'];

/** Espaçamento entre letras do título. */
export const STUDIO_HEADING_TRACKING: Record<string, string> = {
  tight: '-.035em',
  normal: '-.02em',
  wide: '.02em',
  wider: '.12em',
};

/** Tema mínimo consumido por este módulo. */
export interface StudioTypographyTheme {
  fontPreset?: unknown;
  fontFamily?: unknown;
  headingFontFamily?: unknown;
  headingWeight?: unknown;
  headingTransform?: unknown;
  headingTracking?: unknown;
}

/** Só deixa passar valor simples de CSS (sem chaves, ponto e vírgula ou url). */
function cssSeguro(valor: unknown): string {
  const bruto = String(valor ?? '').trim();
  if (!bruto || bruto.length > 120) return '';
  if (/[;{}<>()\\]|url|expression|@import/i.test(bruto)) return '';
  return bruto;
}

/** Resultado já validado, pronto para virar variáveis de CSS. */
export interface StudioTypographyTokens {
  bodyFont: string;
  headingFont: string;
  headingWeight: string;
  headingTransform: string;
  headingTracking: string;
}

/**
 * Resolve a tipografia do documento.
 * A ordem é: família escrita à mão (quando segura) → conjunto aprovado → padrão.
 */
export function resolverTipografia(tema: StudioTypographyTheme | undefined | null): StudioTypographyTokens {
  const t = tema ?? {};
  const preset =
    STUDIO_FONT_PRESETS.find((item) => item.key === String(t.fontPreset ?? '')) ?? STUDIO_FONT_PRESETS[0];

  const corpo = cssSeguro(t.fontFamily) || preset.body;
  const titulo = cssSeguro(t.headingFontFamily) || preset.heading || corpo;

  const peso = STUDIO_HEADING_WEIGHTS.includes(String(t.headingWeight ?? '')) ? String(t.headingWeight) : '700';
  const caixa = STUDIO_HEADING_TRANSFORMS.includes(String(t.headingTransform ?? ''))
    ? String(t.headingTransform)
    : 'none';
  const tracking = STUDIO_HEADING_TRACKING[String(t.headingTracking ?? '')] ?? STUDIO_HEADING_TRACKING.normal;

  return {
    bodyFont: corpo,
    headingFont: titulo,
    headingWeight: peso,
    headingTransform: caixa,
    headingTracking: tracking,
  };
}

/**
 * Variáveis de CSS do documento (usadas na raiz da página publicada e na raiz
 * do canvas do editor, garantindo o mesmo resultado nos dois lugares).
 */
export function variaveisDeTipografia(tema: StudioTypographyTheme | undefined | null): Record<string, string> {
  const tokens = resolverTipografia(tema);
  return {
    '--st-font': tokens.bodyFont,
    '--st-heading-font': tokens.headingFont,
    '--st-heading-weight': tokens.headingWeight,
    '--st-heading-transform': tokens.headingTransform,
    '--st-heading-tracking': tokens.headingTracking,
  };
}

/** Mesma coisa, em texto pronto para um bloco `style` de CSS. */
export function declaracoesDeTipografia(tema: StudioTypographyTheme | undefined | null): string {
  return Object.entries(variaveisDeTipografia(tema))
    .map(([chave, valor]) => `${chave}: ${valor};`)
    .join(' ');
}
